import React, { useState, useEffect } from 'react';
import ExportButtons from './ExportButtons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const tooltipStyle = {
  backgroundColor: 'rgba(18,18,30,0.95)',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  titleColor: '#fff',
  bodyColor: 'rgba(255,255,255,0.8)',
};

const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const SecurityCompliance = () => {
  const [securityScore, setSecurityScore] = useState(94);

  const kpis = [
    { label: 'WAF Blocked Requests', value: '2,847', unit: '/day', icon: 'fas fa-shield-alt', color: '#ef4444' },
    { label: 'Failed Logins', value: '342', unit: '/day', icon: 'fas fa-lock', color: '#f59e0b' },
    { label: 'Rate Limited', value: '1,204', unit: '/day', icon: 'fas fa-hourglass-half', color: '#6366f1' },
    { label: 'Compliance Score', value: '94%', unit: '', icon: 'fas fa-check-circle', color: '#10b981' },
  ];

  return (
    <div className="sc-container">
      <div className="sc-header">
        <div className="sc-header-left">
          <h4>Security & Compliance</h4>
          <p className="sc-subtitle">WAF, authentication, rate limiting & data governance</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExportButtons
            data={[
              { metric: 'WAF Blocked Requests', value: '2,847/day', severity: 'High' },
              { metric: 'Failed Logins', value: '342/day', severity: 'Medium' },
              { metric: 'Rate Limited', value: '1,204/day', severity: 'Medium' },
              { metric: 'Compliance Score', value: '94%', severity: 'Low' },
              { metric: 'SQL Injection Attempts', value: '89', severity: 'Critical' },
              { metric: 'XSS Attempts', value: '234', severity: 'High' },
              { metric: 'DDoS Mitigation', value: '12', severity: 'High' },
              { metric: 'Bot Traffic Blocked', value: '1,543', severity: 'Medium' },
            ]}
            headers={[
              { key: 'metric', label: 'Threat/Metric' }, { key: 'value', label: 'Value' }, { key: 'severity', label: 'Severity' },
            ]}
            title="Security & Compliance Report"
            filename="security_compliance"
          />
        </div>
        <div className="sc-security-badge">
          <span className="sc-badge-label">Security Score</span>
          <span className="sc-badge-value">{securityScore}/100</span>
          <div className="sc-badge-bar">
            <div className="sc-badge-fill" style={{ width: `${securityScore}%` }}></div>
          </div>
        </div>
      </div>

      <div className="sc-kpi-grid">
        {kpis.map((kpi, i) => (
          <div key={i} className="sc-kpi-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="sc-kpi-icon" style={{ background: kpi.color + '15', color: kpi.color }}>
              <i className={kpi.icon}></i>
            </div>
            <div className="sc-kpi-content">
              <div className="sc-kpi-value">{kpi.value}</div>
              <div className="sc-kpi-label">{kpi.label}</div>
              {kpi.unit && <div className="sc-kpi-unit">{kpi.unit}</div>}
            </div>
          </div>
        ))}
      </div>

      <WAFActivityChart />
      <ThreatDetectionTable />
      <AuthenticationMetrics />
      <RateLimitingDashboard />
      <S3BucketSecurity />
      <DataRetentionCompliance />
    </div>
  );
};

const WAFActivityChart = () => {
  const wafData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Blocked Requests',
        data: [2847, 3100, 2945, 3200, 3050, 2800, 2900],
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
      {
        label: 'Allowed Requests',
        data: [45200, 46300, 45900, 47100, 46800, 44500, 45200],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.05)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
    ],
  };

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: v => (v / 1000).toFixed(0) + 'K' }, grid: gridStyle, border: noBorder },
    },
  };

  return (
    <div className="sc-chart-card">
      <div className="sc-chart-head">
        <h6><i className="fas fa-shield-virus"></i> WAF Activity (7 Days)</h6>
        <div className="sc-chart-legend-inline">
          <span><span className="sc-dot" style={{ background: '#ef4444' }}></span> Blocked</span>
          <span><span className="sc-dot" style={{ background: '#10b981' }}></span> Allowed</span>
        </div>
      </div>
      <div className="sc-chart-body" style={{ height: 280 }}>
        <Line data={wafData} options={chartOpts} />
      </div>
    </div>
  );
};

const ThreatDetectionTable = () => {
  const threats = [
    { id: 'THR-2847', type: 'SQL Injection', ip: '192.168.1.105', severity: 'critical', action: 'blocked', timestamp: '2 min ago' },
    { id: 'THR-2846', type: 'XSS Attack', ip: '203.0.113.45', severity: 'high', action: 'rate-limited', timestamp: '8 min ago' },
    { id: 'THR-2845', type: 'DDoS', ip: '198.51.100.89', severity: 'critical', action: 'blocked', timestamp: '15 min ago' },
    { id: 'THR-2844', type: 'Bot Activity', ip: '203.0.113.112', severity: 'medium', action: 'flagged', timestamp: '22 min ago' },
    { id: 'THR-2843', type: 'Brute Force', ip: '192.0.2.201', severity: 'high', action: 'blocked', timestamp: '31 min ago' },
    { id: 'THR-2842', type: 'SQL Injection', ip: '198.51.100.56', severity: 'medium', action: 'rate-limited', timestamp: '45 min ago' },
  ];

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#eab308';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getActionColor = (action) => {
    switch(action) {
      case 'blocked': return '#ef4444';
      case 'rate-limited': return '#f59e0b';
      case 'flagged': return '#6366f1';
      default: return '#10b981';
    }
  };

  return (
    <div className="sc-chart-card">
      <div className="sc-chart-head">
        <h6><i className="fas fa-exclamation-triangle"></i> Threat Detection</h6>
        <span className="sc-badge-sm">{threats.length} threats detected</span>
      </div>
      <div className="sc-table-wrapper">
        <table className="sc-threat-table">
          <thead>
            <tr>
              <th>Threat ID</th>
              <th>Type</th>
              <th>Source IP</th>
              <th>Severity</th>
              <th>Action Taken</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {threats.map((t, i) => (
              <tr key={i}>
                <td><span className="sc-threat-id">{t.id}</span></td>
                <td><span className="sc-threat-type">{t.type}</span></td>
                <td><span className="sc-ip-badge">{t.ip}</span></td>
                <td>
                  <span className="sc-severity-badge" style={{ background: getSeverityColor(t.severity) + '20', color: getSeverityColor(t.severity) }}>
                    {t.severity}
                  </span>
                </td>
                <td>
                  <span className="sc-action-badge" style={{ background: getActionColor(t.action) + '20', color: getActionColor(t.action) }}>
                    {t.action}
                  </span>
                </td>
                <td><span className="sc-timestamp">{t.timestamp}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AuthenticationMetrics = () => {
  const authMetrics = [
    { label: 'JWT Avg Token Lifetime', value: '15min', icon: 'fas fa-clock', color: '#6366f1' },
    { label: 'Refresh Token Rotations', value: '24K/day', icon: 'fas fa-sync', color: '#f82581' },
    { label: 'Active Sessions', value: '12,800', icon: 'fas fa-users', color: '#10b981' },
    { label: 'MFA Adoption', value: '34%', icon: 'fas fa-shield', color: '#f59e0b' },
    { label: 'Password Hash', value: 'bcrypt-12', icon: 'fas fa-lock', color: '#8b5cf6' },
    { label: 'Session Timeout', value: '30min', icon: 'fas fa-hourglass-end', color: '#ec4899' },
  ];

  return (
    <div className="sc-chart-card">
      <div className="sc-chart-head">
        <h6><i className="fas fa-user-check"></i> Authentication Metrics</h6>
      </div>
      <div className="sc-auth-metrics-grid">
        {authMetrics.map((m, i) => (
          <div key={i} className="sc-auth-metric" style={{ animationDelay: `${i * 0.05}s` }}>
            <div className="sc-auth-icon" style={{ background: m.color + '15', color: m.color }}>
              <i className={m.icon}></i>
            </div>
            <div className="sc-auth-content">
              <div className="sc-auth-value">{m.value}</div>
              <div className="sc-auth-label">{m.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RateLimitingDashboard = () => {
  const rateLimitData = {
    labels: ['/api/auth', '/api/search', '/api/stream', '/api/payment', '/api/content'],
    datasets: [{
      label: 'Rate Limit Hits',
      data: [450, 320, 180, 95, 65],
      backgroundColor: ['#ef4444', '#f59e0b', '#6366f1', '#10b981', '#8b5cf6'],
      borderRadius: 6,
      borderSkipped: false,
      barPercentage: 0.7,
    }],
  };

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: v => v.toLocaleString() }, grid: gridStyle, border: noBorder },
    },
  };

  return (
    <div className="sc-chart-card">
      <div className="sc-chart-head">
        <h6><i className="fas fa-tachometer-alt"></i> Rate Limiting Dashboard</h6>
        <span className="sc-badge-sm">Per Endpoint</span>
      </div>
      <div className="sc-chart-body" style={{ height: 260 }}>
        <Bar data={rateLimitData} options={chartOpts} />
      </div>
    </div>
  );
};

const S3BucketSecurity = () => {
  const buckets = [
    { name: 'dramamix-videos', access: 'Private', encryption: 'AES-256', size: '2.4TB', status: 'secure' },
    { name: 'dramamix-thumbnails', access: 'CloudFront', encryption: 'AES-256', size: '180GB', status: 'secure' },
    { name: 'dramamix-backups', access: 'Private', encryption: 'AES-256', size: '890GB', extra: 'Encrypted Backups', status: 'secure' },
    { name: 'dramamix-logs', access: 'Private', encryption: 'AES-256', size: '45GB', extra: '90-day retention', status: 'secure' },
  ];

  return (
    <div className="sc-chart-card">
      <div className="sc-chart-head">
        <h6><i className="fas fa-database"></i> S3 Bucket Security</h6>
        <span className="sc-badge-sm">{buckets.length} buckets</span>
      </div>
      <div className="sc-bucket-grid">
        {buckets.map((b, i) => (
          <div key={i} className="sc-bucket-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="sc-bucket-header">
              <h5 className="sc-bucket-name">{b.name}</h5>
              <span className="sc-bucket-status" style={{ color: '#10b981' }}>
                <i className="fas fa-check-circle"></i> {b.status}
              </span>
            </div>
            <div className="sc-bucket-info">
              <div className="sc-bucket-item">
                <span className="sc-bucket-label">Access</span>
                <span className="sc-bucket-value">{b.access}</span>
              </div>
              <div className="sc-bucket-item">
                <span className="sc-bucket-label">Encryption</span>
                <span className="sc-bucket-value">{b.encryption}</span>
              </div>
              <div className="sc-bucket-item">
                <span className="sc-bucket-label">Size</span>
                <span className="sc-bucket-value">{b.size}</span>
              </div>
              {b.extra && (
                <div className="sc-bucket-item">
                  <span className="sc-bucket-label">Policy</span>
                  <span className="sc-bucket-value">{b.extra}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DataRetentionCompliance = () => {
  const compliance = [
    { framework: 'GDPR', status: 'Compliant', icon: 'fas fa-check-circle' },
    { framework: 'CCPA', status: 'Compliant', icon: 'fas fa-check-circle' },
    { framework: 'SOC 2', status: 'In Progress', icon: 'fas fa-spinner' },
    { framework: 'PCI DSS', status: 'Compliant', icon: 'fas fa-check-circle' },
  ];

  const policies = [
    { name: 'Data Retention Policy', detail: '90 days logs, 365 days user data' },
    { name: 'Right to Deletion', detail: 'Automated, <72h' },
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'Compliant': return '#10b981';
      case 'In Progress': return '#f59e0b';
      default: return '#ef4444';
    }
  };

  return (
    <div className="sc-chart-card">
      <div className="sc-chart-head">
        <h6><i className="fas fa-file-contract"></i> Data Retention & Compliance</h6>
      </div>
      <div className="sc-compliance-wrapper">
        <div className="sc-compliance-section">
          <h5 className="sc-compliance-title">Compliance Frameworks</h5>
          <div className="sc-compliance-list">
            {compliance.map((c, i) => (
              <div key={i} className="sc-compliance-item">
                <div className="sc-compliance-header">
                  <span className="sc-compliance-name">{c.framework}</span>
                  <span className="sc-compliance-status" style={{ color: getStatusColor(c.status) }}>
                    <i className={c.icon}></i> {c.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sc-compliance-section">
          <h5 className="sc-compliance-title">Policies</h5>
          <div className="sc-policies-list">
            {policies.map((p, i) => (
              <div key={i} className="sc-policy-item">
                <div className="sc-policy-header">
                  <span className="sc-policy-name">{p.name}</span>
                </div>
                <span className="sc-policy-detail">{p.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityCompliance;
